import { useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { SurveyRepositoryImpl } from "../../data/repository/survey_repository_impl";
import { useMemo } from "react";
import { SurveyInterface } from "../interfaces/survey_interface";
import { useOutletContext } from "react-router-dom";
import { UserInterface } from "../../../../core/components/user/domain/interfaces/user_interface";

interface PaginatedSurveysResponse {
    surveys: SurveyInterface[];
    fetchedAll: boolean;
}

export const useGetSurveys = (limit: number = 10) => {
    const repository = new SurveyRepositoryImpl();
    const { company } = useOutletContext<UserInterface>();
    
    const query = useInfiniteQuery<PaginatedSurveysResponse, AxiosError>({
        queryKey: ['surveysList', limit, company?.id],
        queryFn: async ({ pageParam = 0, }) => {
            const result = await repository.getSurveys(pageParam as number, limit);
            
            return {
                surveys: result.surveys,
                fetchedAll: result.fetchedAll
            };
        },
        retry: 4,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: true,
        getNextPageParam: (lastPage, allPages) => {
            const currentPageNumber = allPages.length;

            if (!lastPage.fetchedAll) {
                return currentPageNumber * limit;
            }
            return undefined;
        },
        initialPageParam: 0,
    });

    const allSurveys = useMemo(() => {
        const surveys = query.data?.pages.flatMap(page => page.surveys) || [];
        return surveys;
    }, [query.data]);

    return { ...query, allSurveys };
};